




























































































































































  import { defineComponent, ref, computed, watch } from 'vue'
  import { Orderpath, Products } from '@one/types'
  import { useCompareStore } from '~/stores/useCompareStore'
  import useCurrentInstance from '~/utils/useCurrentInstance'
  import useAddToCart from '~/composables/useAddToCart'
  import useCoreUtils from '~/utils/useUtils'

  export default defineComponent({
    name: 'Compare',
    setup() {
      const vm = useCurrentInstance()
      const compareStore = useCompareStore()
      const { addToCart } = useAddToCart(vm)
      const { priceTypeLabel } = useCoreUtils(vm)

      const isLoading = ref(false)
      const fullProducts = ref<any[]>([])
      const highlightDifferences = ref(false)

      const disabledAddToCart = (product) => {
        if (!product) return false
        return vm.$store.getters['stocks/isEnoughProductWithBlockLabelInStock'](
          1,
          product.labels,
          product.id,
        )
      }

      const fetchCompareProducts = async () => {
        const idsToFetch = compareStore.products.map((p: any) => p.id)

        if (!idsToFetch.length) {
          fullProducts.value = []
          return
        }

        isLoading.value = true
        try {
          const fetchedData = await vm.$store.dispatch('products/fetchProductsById', idsToFetch)
          await vm.$store.dispatch('products/fetchPricesForProducts', idsToFetch)

          const sortedFetchedData = idsToFetch
            .map((id) => fetchedData.find((p: any) => p.id === id))
            .filter(Boolean)

          fullProducts.value = sortedFetchedData

          if (sortedFetchedData && sortedFetchedData.length) {
            compareStore.setProducts(sortedFetchedData)
          }
        } catch (error) {
          vm.$logger.error(error)
        } finally {
          isLoading.value = false
        }
      }

      watch(
        () => compareStore.products.length,
        () => {
          fetchCompareProducts()
        },
        { immediate: true },
      )

      const removeProduct = (id: string) => {
        compareStore.removeById(id)
      }

      const carts = computed(() => {
        if (vm.$auth.isAuthenticated) {
          return Object.keys(vm.$store.getters['cart/getBasicCarts']).map((id: string) =>
            vm.$store.getters['cart/getBasicCartById'](id),
          )
        }
        return (
          (vm.$store.getters['cart/getCurrentCart'] && [
            {
              id: vm.$store.getters['cart/getCurrentCartId'],
              name: vm.$store.getters['cart/getCurrentCart'].name,
            },
          ]) ||
          []
        )
      })

      const wishlists = computed(() => {
        if (vm.$auth.isAuthenticated) {
          return Object.keys(vm.$store.getters['wishlist/getBasicWishlists']).map((id: string) =>
            vm.$store.getters['wishlist/getBasicWishlistById'](id),
          )
        }
        return (
          (vm.$store.getters['wishlist/getCurrentWishlist'] && [
            {
              id: vm.$store.getters['wishlist/getCurrentWishlistId'],
              name: vm.$store.getters['wishlist/getCurrentWishlist'].name,
            },
          ]) ||
          []
        )
      })

      const allUniqueCharacteristics = computed(() => {
        const charCounts = new Map<string, number>()
        const totalProducts = fullProducts.value.length

        fullProducts.value.forEach((product) => {
          if (product.characteristics && Array.isArray(product.characteristics)) {
            product.characteristics.forEach((char: any) => {
              const currentCount = charCounts.get(char.characteristicName) || 0
              charCounts.set(char.characteristicName, currentCount + 1)
            })
          }
        })

        return Array.from(charCounts.keys()).sort((a, b) => {
          const isAllA = charCounts.get(a) === totalProducts
          const isAllB = charCounts.get(b) === totalProducts

          if (isAllA && !isAllB) return -1
          if (!isAllA && isAllB) return 1

          return a.localeCompare(b, 'pl')
        })
      })

      const getCurrency = computed(() => vm.$store.getters['layout/getCurrency'])

      const price = (product) => {
        return (
          product &&
          vm.$store.getters['products/getProductPrice'](
            product.id,
            vm.$store.getters['layout/isGrossSelected'],
          )
        )
      }

      const productUnit = (product) => {
        return product && vm.$store.getters['products/getProductOrderUnit'](product.id)
      }

      const getCharacteristicValue = (product: any, charName: string) => {
        if (!product.characteristics) return null
        const char = product.characteristics.find((c: any) => c.characteristicName === charName)
        return char ? char.value : null
      }

      const hasCharacteristicDifference = (charName: string) => {
        if (fullProducts.value.length < 2) return false
        const firstVal = getCharacteristicValue(fullProducts.value[0], charName)
        return fullProducts.value.some((p) => getCharacteristicValue(p, charName) !== firstVal)
      }

      const allUniqueAttributes = computed(() => {
        const PREFIX_TO_HIDE = 'hidden_'
        const attrCounts = new Map<string, number>()
        const totalProducts = fullProducts.value.length

        fullProducts.value.forEach((product) => {
          if (product.attributes && Array.isArray(product.attributes)) {
            product.attributes
              .filter((attr: any) => !attr.nameCode?.includes(PREFIX_TO_HIDE))
              .forEach((attr: any) => {
                const currentCount = attrCounts.get(attr.name) || 0
                attrCounts.set(attr.name, currentCount + 1)
              })
          }
        })

        return Array.from(attrCounts.keys()).sort((a, b) => {
          const isAllA = attrCounts.get(a) === totalProducts
          const isAllB = attrCounts.get(b) === totalProducts

          if (isAllA && !isAllB) return -1
          if (!isAllA && isAllB) return 1

          return a.localeCompare(b, 'pl')
        })
      })

      const getAttributeValue = (product: any, attrName: string) => {
        if (!product.attributes) {
          return null
        }
        const attr = product.attributes.find((a: any) => a.name === attrName)
        if (!attr) {
          return null
        }
        if (attr.valueType === Products.AttributeType.LOGIC) {
          return attr.valueCode
        }
        return attr.value
      }

      const isBooleanAttribute = (product: any, attrName: string) => {
        return typeof getAttributeValue(product, attrName) === 'boolean'
      }

      const getBooleanAttributeIconClass = (product: any, attrName: string) => {
        return getAttributeValue(product, attrName) ? 'icon-correct' : 'icon-cancel'
      }

      const hasAttributeDifference = (attrName: string) => {
        if (fullProducts.value.length < 2) return false
        const firstVal = getAttributeValue(fullProducts.value[0], attrName)
        return fullProducts.value.some((p) => getAttributeValue(p, attrName) !== firstVal)
      }

      const hasDifference = (key: string) => {
        if (fullProducts.value.length < 2) return false

        const getValue = (p: any) => {
          switch (key) {
            case 'quantityMin':
              return p.measurementUnits?.quantityMin
            case 'quantityInterval':
              return p.measurementUnits?.quantityInterval
            default:
              return p[key]
          }
        }

        const firstVal = getValue(fullProducts.value[0])
        return fullProducts.value.some((p) => getValue(p) !== firstVal)
      }

      const addCompareProductToCart = (id, quantity) => {
        addToCart(id, quantity, 'compare', vm.$store.getters['cart/getCurrentCartId'])
      }

      const onAddToWishlist = (
        wishlist: Orderpath.App.Wishlist.Responses.BasicWishlist | undefined,
        productId: string,
        amountOfProducts: number,
      ) => {
        const targetWishlistId = wishlist?.id || vm.$store.getters['wishlist/getCurrentWishlistId']

        vm.$store
          .dispatch('wishlist/addProductToWishlist', {
            wishlistId: targetWishlistId,
            newProduct: {
              productId: productId,
              amountOfProducts: amountOfProducts,
            },
          })
          .then(() => {
            vm.$notify({
              group: 'alert',
              type: 'success',
              text: vm.$t('product_page.alert_added_to_wishlist') as string,
            })

            const addedProduct =
              vm.$store.getters['wishlist/getWishlistProduct'](targetWishlistId)(productId)
            if (addedProduct) {
              vm.$gtm.addToWishlist(
                {
                  item_id: addedProduct.id,
                  item_name: addedProduct.name,
                  item_brand: addedProduct.brand?.name || '',
                  item_list_name:
                    vm.$store.getters['wishlist/getCurrentWishlist']?.name || 'Default',
                  category: vm.$routes.getCategoryPath(addedProduct.canonicalCategory) || '',
                  price: addedProduct.prices?.unitNet || 0,
                  discount:
                    (addedProduct.prices?.catalogPriceNet || 0) -
                    (addedProduct.prices?.unitNet || 0),
                  quantity: amountOfProducts || 0,
                },
                (addedProduct.prices?.unitNet || 0) * (amountOfProducts || 0),
              )
            }
          })
          .catch((err: any) => {
            vm.$logger.warn(err)
            vm.$notify({
              group: 'alert',
              type: 'error',
              text: vm.$t('errors.add_to_wishlist_error') as string,
            })
          })
      }

      return {
        isLoading,
        fullProducts,
        highlightDifferences,
        priceTypeLabel,
        allUniqueCharacteristics,
        getCharacteristicValue,
        hasCharacteristicDifference,
        allUniqueAttributes,
        getAttributeValue,
        isBooleanAttribute,
        getBooleanAttributeIconClass,
        hasAttributeDifference,
        disabledAddToCart,
        carts,
        wishlists,
        getCurrency,
        hasDifference,
        removeProduct,
        addCompareProductToCart,
        onAddToWishlist,
        price,
        productUnit,
      }
    },
    asyncData({ store, redirect }) {
      const isCompareEnabled = store.getters['cms/configuration']?.showCompareProducts

      if (!isCompareEnabled) {
        return redirect('/')
      }
    },
  })
